// IncomingWebhookSetupModal.jsx

import React from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Components
import ModalActions from './ModalActions';
import Button from 'components/button/Button';
import Modal from 'components/modal/Modal';

/**
 * IncomingWebhookSetupModal
 *
 * Renders setup details for an existing incoming webhook.
 *
 * @param {object} theme - Active theme palette values.
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {function} onClose - Handler to close the modal.
 * @param {string} selectedIncomingHookUrl - Selected webhook url.
 * @param {string} selectedIncomingHeaderPreview - Header template for selected webhook.
 * @param {array} selectedIncomingFieldRows - Payload field rows available for selected type.
 * @param {string} incomingSecretHeaderName - Required incoming secret header name.
 * @param {function} onCopyToClipboard - Handler to copy values.
 *
 * @returns {ReactNode} The rendered incoming webhook setup modal.
 */
const IncomingWebhookSetupModal = ({
    theme,
    isOpen,
    onClose,
    selectedIncomingHookUrl,
    selectedIncomingHeaderPreview,
    selectedIncomingFieldRows,
    incomingSecretHeaderName,
    onCopyToClipboard,
}) => {

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  // ----- RENDER ----- //

  return (
        <Modal
          title="Webhook Setup"
          isOpen={isOpen}
          width={isMobile ? '100%' : 620}
          maxHeight="85vh"
          onClose={onClose}
        >
            <div className="api-integrations-modal-body">
                <div
                  className="api-integrations-subtitle"
                  style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Use this reference when wiring tools like Zapier. Existing webhook secrets are never re-shown.
                </div>

                <div className="api-integrations-form-grid">
                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Webhook URL
                    </div>
                    <div
                      className="api-integrations-static-field"
                      style={{
                            color: theme.foreground,
                        }}
                    >
                        {selectedIncomingHookUrl || 'Unavailable'}
                    </div>

                    <div
                      className="api-integrations-field-label"
                      style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Required Header
                    </div>
                    <div
                      className="api-integrations-static-field api-integrations-code"
                      style={{
                            color: theme.foreground,
                        }}
                    >
                        {selectedIncomingHeaderPreview}
                    </div>
                </div>

                <div
                  className="api-integrations-field-map"
                  style={{
                        borderColor: theme.backgroundMuted,
                    }}
                >
                    <div
                      className="api-integrations-field-map-title"
                      style={{
                            color: theme.foreground,
                        }}
                    >
                        Available Payload Fields
                    </div>

                    {selectedIncomingFieldRows.length === 0 && (
                        <div
                          className="api-integrations-empty"
                          style={{
                                color: theme.foregroundMuted,
                            }}
                        >
                            No fields found for this type.
                        </div>
                    )}

                    {selectedIncomingFieldRows.map((field) => (
                        <div
                          className="api-integrations-field-map-row"
                          style={{
                                borderColor: theme.backgroundMuted,
                            }}
                          key={field.fieldId}
                        >
                            <div
                              className="api-integrations-field-map-slug"
                              style={{
                                    color: theme.foreground,
                                }}
                            >
                                {field.slug}
                            </div>
                            <div
                              className="api-integrations-field-map-meta"
                              style={{
                                    color: theme.foregroundMuted,
                                }}
                            >
                                {field.label} ({field.type})
                            </div>
                        </div>
                    ))}
                </div>

                <ModalActions>
                    <Button
                      label="Copy URL"
                      enabled={Boolean(selectedIncomingHookUrl)}
                      height="30px"
                      fontSize="12px"
                      borderRadius="6px"
                      onClick={() => onCopyToClipboard(selectedIncomingHookUrl, 'Incoming webhook URL copied.')}
                    />
                    <Button
                      label="Copy Header"
                      enabled={Boolean(incomingSecretHeaderName)}
                      height="30px"
                      fontSize="12px"
                      borderRadius="6px"
                      onClick={() => onCopyToClipboard(incomingSecretHeaderName, 'Incoming header name copied.')}
                    />
                    <Button
                      label="Copy Header Template"
                      enabled={Boolean(selectedIncomingHeaderPreview)}
                      height="30px"
                      fontSize="12px"
                      borderRadius="6px"
                      onClick={() => onCopyToClipboard(selectedIncomingHeaderPreview, 'Incoming header template copied.')}
                    />
                </ModalActions>
            </div>
        </Modal>
    );
};

// Dev Tools
IncomingWebhookSetupModal.displayName = 'IncomingWebhookSetupModal';

export default IncomingWebhookSetupModal;
